import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/** Cleared when the tab/window session ends — intro can run again on the next visit. */
const SESSION_KEY = 'bfp_logo_intro_done_session';

function shouldSkipPath(pathname) {
  return pathname.startsWith('/admin') || pathname.startsWith('/vendor');
}

/**
 * Session intro: logo.mp4 on coral background.
 * Browsers block unmuted autoplay without a user gesture — we ask once per session with
 * an explicit "Play with sound" control, then play unmuted. Completed intro is stored in
 * sessionStorage only so it does not repeat during the same session (SPA navigations).
 */
const LogoIntroOverlay = () => {
  const { pathname } = useLocation();
  const videoRef = useRef(null);
  const [visible, setVisible] = useState(() => {
    try {
      if (typeof window === 'undefined') return false;
      if (shouldSkipPath(window.location.pathname)) return false;
      return !window.sessionStorage.getItem(SESSION_KEY);
    } catch {
      return false;
    }
  });
  const [fadeOut, setFadeOut] = useState(false);
  /** Until true, video stays paused — user taps "Play with sound" (valid gesture for audio). */
  const [soundStarted, setSoundStarted] = useState(false);

  const markSessionDone = useCallback(() => {
    try {
      window.sessionStorage.setItem(SESSION_KEY, '1');
    } catch {
      // private mode etc.
    }
  }, []);

  const dismiss = useCallback(() => {
    markSessionDone();
    const v = videoRef.current;
    if (v) {
      v.pause();
    }
    setFadeOut(true);
    window.setTimeout(() => setVisible(false), 380);
  }, [markSessionDone]);

  const startWithSound = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = false;
    v.defaultMuted = false;
    v.volume = 1;
    setSoundStarted(true);
    const p = v.play();
    if (p && typeof p.then === 'function') {
      p.catch(() => {
        /* unlikely after gesture; keep UI usable */
        v.muted = true;
        v.play().catch(() => {});
      });
    }
  }, []);

  useEffect(() => {
    if (!visible) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [visible]);

  useEffect(() => {
    if (shouldSkipPath(pathname)) {
      setVisible(false);
    }
  }, [pathname]);

  if (!visible) return null;

  const src = `${process.env.PUBLIC_URL || ''}/assets/logo.mp4`;

  return (
    <div
      className={`fixed inset-0 z-[9999] flex flex-col items-center justify-center md:bg-[#FE6956] bg-[#FF5E4C] rounded-none transition-opacity duration-300 ease-out ${
        fadeOut ? 'pointer-events-none opacity-0' : 'opacity-100'
      }`}
      role="dialog"
      aria-modal="true"
      aria-label="Welcome"
    >
      <div className="relative flex w-full max-w-3xl flex-1 flex-col items-center justify-center py-8">
        <video
          ref={videoRef}
          className="max-h-[min(72vh,560px)] w-full max-w-[min(90vw,720px)] object-contain"
          src={src}
          playsInline
          preload="auto"
          muted={false}
          onEnded={dismiss}
          onError={dismiss}
        />

        {!soundStarted && !fadeOut && (
          <div
            className="absolute inset-0 flex flex-col items-center justify-center gap-5 bg-black/45 px-4 text-center backdrop-blur-[2px]"
            aria-live="polite"
          >
            <p className="max-w-sm text-[15px] font-medium leading-snug text-white drop-shadow-sm md:text-[16px]">
              Play the welcome video with sound. Your browser only allows audio after you tap
              once.
            </p>
            <button
              type="button"
              onClick={startWithSound}
              className="rounded-full bg-white px-7 py-3 text-[15px] font-semibold text-[#FF5E4C] shadow-md transition hover:bg-[#FFF5F3] focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#FF5E4C]"
            >
              Play with sound
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default LogoIntroOverlay;
